import { useEffect, useState, useRef } from "react"
import { Link } from "react-router"
import { fetchRecentScans } from "../../lib/api"

const POLL_INTERVAL_MS = 30 * 1000

// Recent scans feed. Owns its own fetch + polling — the dashboard
// only mounts it. Polls every 30s while document.visibilityState
// is "visible"; pauses on hidden, refreshes on visible. Hides
// itself entirely (returns null) when the account has zero
// lifetime scans, signalled by the parent via the `accountHasScans`
// prop (so we don't need an extra round-trip just to decide
// visibility).
export default function RecentScansFeed({ accountHasScans }) {
  const [scans, setScans] = useState(null) // null = loading; [] = loaded but empty
  const timerRef = useRef(null)

  useEffect(() => {
    if (!accountHasScans) return

    let cancelled = false

    const load = async () => {
      try {
        const data = await fetchRecentScans({ limit: 10 })
        if (!cancelled) setScans(data)
      } catch (e) {
        // Silent — a polling failure isn't worth nagging about. Old data stays.
      }
    }

    const start = () => {
      load()
      timerRef.current = setInterval(load, POLL_INTERVAL_MS)
    }
    const stop = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        start()
      } else {
        stop()
      }
    }

    if (document.visibilityState === "visible") start()
    document.addEventListener("visibilitychange", handleVisibility)

    return () => {
      cancelled = true
      stop()
      document.removeEventListener("visibilitychange", handleVisibility)
    }
  }, [accountHasScans])

  if (!accountHasScans) return null

  return (
    <div className="bg-white dark:bg-midnight border border-[color:var(--color-border)] dark:border-storm rounded-lg p-4">
      <div className="text-xs uppercase tracking-wider text-[color:var(--color-muted)] dark:text-muted-light font-semibold mb-3">
        Recent scans
      </div>
      {scans === null && (
        <div className="text-xs text-[color:var(--color-muted)] dark:text-muted-light py-2">loading…</div>
      )}
      {scans && scans.length === 0 && (
        <div className="text-xs text-[color:var(--color-muted)] dark:text-muted-light py-2">No scans yet.</div>
      )}
      {scans && scans.length > 0 && (
        <ul className="space-y-1.5">
          {scans.map((s, i) => (
            <li key={`${s.code_token}-${s.scanned_at}-${i}`}>
              <Link
                to={`/app/codes/${s.code_token}`}
                className="flex items-center gap-2 text-xs text-ink dark:text-cloud hover:text-voltage-dark dark:hover:text-voltage-light transition"
              >
                <span aria-hidden="true">{flagFor(s.country_code)}</span>
                {/* The code's name, as in the codes list: text, not a data value. */}
                <span className="truncate flex-1 min-w-0" title={s.code_label}>{s.code_label}</span>
                <span className="text-[color:var(--color-muted)] dark:text-muted-light flex-shrink-0">
                  {relativeTime(s.scanned_at)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// Country code → emoji flag. ISO 3166-1 alpha-2 codes map to the
// regional indicator block (U+1F1E6 + offset). Fall back to a globe
// when the code is missing or malformed.
function flagFor(countryCode) {
  if (!countryCode || countryCode.length !== 2) return "🌐"
  const offset = 0x1f1e6 - 65
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((c) => c.charCodeAt(0) + offset)
  return String.fromCodePoint(...codePoints)
}

// Compact relative time formatter — seconds, minutes, hours, days,
// then fall back to a date. Avoids pulling in a date-fns dep for
// what's a one-shot label.
function relativeTime(iso) {
  const then = new Date(iso).getTime()
  const now  = Date.now()
  const sec  = Math.max(0, Math.floor((now - then) / 1000))
  if (sec < 60)     return `${sec}s`
  if (sec < 3600)   return `${Math.floor(sec / 60)}m`
  if (sec < 86400)  return `${Math.floor(sec / 3600)}h`
  if (sec < 604800) return `${Math.floor(sec / 86400)}d`
  return new Date(iso).toLocaleDateString()
}
